import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminShell } from '@/apps/coach/components/admin/AdminShell';
import { routes } from '@/lib/routes';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

type VoucherDetail = Awaited<ReturnType<typeof api.operator.vouchers.get>>;

function getModeLabel(v: { scope: 'per_user' | 'global'; maxRedemptions: number | null }): string {
  if (v.scope === 'global') return 'Gemeinsames Budget';
  if (v.maxRedemptions === 1) return 'Einzelcode';
  return 'Pro-Nutzer-Budget';
}

export function VoucherDetail() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState<VoucherDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!code) return;
    try {
      const data = await api.operator.vouchers.get(code);
      setVoucher(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden');
    }
  }

  useEffect(() => { load(); }, [code]);

  async function handleRevoke() {
    if (!voucher || !confirm(`Gutschein ${voucher.code} wirklich widerrufen?`)) return;
    try {
      await api.operator.vouchers.revoke(voucher.code);
      toast.success('Widerrufen');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fehler');
    }
  }

  if (error) {
    return (
      <AdminShell pageTitle="Voucher-Details">
        <PageHeader title="Gutschein" showBack />
        <div className="flex-1 px-4 py-6"><p className="text-sm text-destructive">{error}</p></div>
      </AdminShell>
    );
  }

  if (!voucher) {
    return (
      <AdminShell pageTitle="Voucher-Details">
        <PageHeader title="Gutschein" showBack />
        <div className="flex-1 px-4 py-6"><p className="text-sm text-muted-foreground">Lade...</p></div>
      </AdminShell>
    );
  }

  return (
    <AdminShell pageTitle="Voucher-Details">
      <PageHeader title={voucher.code} showBack />
      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
        {/* Meta */}
        <section className="rounded-xl border border-border px-4 py-3 space-y-2">
          <div className="flex justify-between items-start gap-2">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-mono text-sm font-semibold">{voucher.code}</p>
                {voucher.isStarter && (
                  <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    Starter
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{getModeLabel(voucher)}</p>
              {voucher.campaignTag && (
                <p className="text-xs text-muted-foreground">Kampagne: {voucher.campaignTag}</p>
              )}
            </div>
            {voucher.revokedAt ? (
              <span className="text-xs text-muted-foreground italic">widerrufen</span>
            ) : (
              <Button size="sm" variant="ghost" onClick={handleRevoke}>Widerrufen</Button>
            )}
          </div>
          <div className="text-xs text-muted-foreground space-y-0.5 pt-1 border-t border-border/60">
            <p>
              {voucher.scope === 'global'
                ? `${voucher.totalUsed} von ${voucher.usageLimit} Nachrichten (gemeinsam)`
                : `Je ${voucher.usageLimit} Nachrichten pro Einlöser · insgesamt ${voucher.totalUsed} verbraucht`}
            </p>
            <p>
              {voucher.redemptions.length} Einlösung(en)
              {voucher.maxRedemptions != null && ` von max. ${voucher.maxRedemptions}`}
            </p>
            {voucher.expiresAt && (
              <p>Läuft ab am {new Date(voucher.expiresAt).toLocaleDateString()}</p>
            )}
            <p>Erstellt am {new Date(voucher.createdAt).toLocaleDateString()}</p>
          </div>
        </section>

        {/* Redemptions */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground">Einlöser</h2>
          {voucher.redemptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch niemand hat diesen Code eingelöst.</p>
          ) : (
            <div className="space-y-2">
              {voucher.redemptions.map((r) => (
                <div key={r.userId + r.redeemedAt} className="rounded-xl border border-border px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.userName || r.userEmail}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.userEmail}</p>
                    </div>
                    {r.replacedAt ? (
                      <span className="shrink-0 text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                        Abgelöst
                      </span>
                    ) : (
                      <span className="shrink-0 text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        Aktiv
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {r.usedCount} Nachrichten · eingelöst am {new Date(r.redeemedAt).toLocaleDateString()}
                    {r.replacedAt && ` · abgelöst am ${new Date(r.replacedAt).toLocaleDateString()}`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <Button variant="outline" onClick={() => navigate(routes.coach.operator.vouchers())}>
          Zurück zur Liste
        </Button>
      </div>
    </AdminShell>
  );
}
